import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ApiServices from "../../Utils/ApiServices/ApiServices";
import SignUpAccessToken from "./SignUpAccessToken";
import CryptoJS from "crypto-js";
import { Link, useNavigate } from "react-router-dom";

/* ================= OTP INPUT ================= */
const OtpInput = ({ otp, setOtp }) => {
  const refs = useRef([]);

  const handleChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;
    setOtp(paste.split(""));
  };

  return (
    <Box onPaste={handlePaste} sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
      {[...Array(6)].map((_, i) => (
        <TextField
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={otp[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
          sx={{ width: 46 }}
        />
      ))}
    </Box>
  );
};

/* ================= SIGN UP ================= */
export default function PatientSignUp() {
  const navigate = useNavigate();
  const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;

  const [values, setValues] = useState({
    title: "", fname: "", mname: "", lname: "",
    street: "", postal_code: "", city: "", state: "",
    phone_contact: "", DOB: "", sex: "", ethnicity: "",
    email: "", height: "", weight: "", terms: false,
  });

  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [otpDialog, setOtpDialog] = useState(false);
  const [otp, setOtp] = useState([]);
  const [createCredentialsLink, setCreateCredentialsLink] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, msg: "", type: "error" });

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 5);
  const maxDateString = maxDate.toISOString().split("T")[0];

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setValues({ ...values, [name]: type === "checkbox" ? checked : value });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const e = {};
    Object.keys(values).forEach((k) => {
      if (!values[k] && !["mname", "ethnicity", "terms"].includes(k)) {
        e[k] = "Required";
      }
    });

    if (!values.terms) e.terms = "Accept terms";
    if (!password || password !== verifyPassword) e.password = "Passwords mismatch";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const verifyRes = await ApiServices.post("/apis/default/api/verifyemail", {
        fname: values.fname,
        mname: values.mname,
        lname: values.lname,
        email: values.email,
        DOB: values.DOB,
      });

      const otpCode = verifyRes.data.data.OTP;
      const tokenRes = await ApiServices.get(`/apis/default/api/verificationtoken/${otpCode}`);
      const tokenId = tokenRes.data.data.token_id_holder;

      const accessToken = await SignUpAccessToken.fetchAccessToken();

      const patientRes = await ApiServices.post(
        "/apis/default/api/patient",
        { ...values, token_id: tokenId },
        { Authorization: `Bearer ${accessToken}` }
      );

      const passLinkRes = await ApiServices.post("/apis/default/api/patientpasslink", {
        resetPass: false,
        email: values.email,
        token_id: patientRes.data.data.token_id,
        pid: patientRes.data.data.pid,
      });

      setCreateCredentialsLink(passLinkRes.data.data.forward);

      await ApiServices.post(
        "/apis/default/api/patientneworresetpass",
        {
          forward: passLinkRes.data.data.forward,
          email: values.email,
          login_uname: values.email,
          pass_new: password,
          pass_new_confirm: password,
        },
        { Authorization: `Bearer ${accessToken}` }
      );

      setOtpDialog(true);
    } catch {
      setSnackbar({ open: true, msg: "Registration failed", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ================= OTP VERIFY ================= */
  useEffect(() => {
    if (otp.length === 6) verifyOtp();
  }, [otp]);

  const verifyOtp = async () => {
    try {
      await ApiServices.post("/apis/default/api/patientneworresetpassupdate", {
        forward: createCredentialsLink,
        onetime_pass: otp.join(""),
        login_uname: values.email,
        email: values.email,
      });

      const { data } = await ApiServices.post(
        "/oauth2/default/token",
        {
          user_role: "patient",
          username: values.email,
          email: values.email,
          password,
          client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
          scope: process.env.REACT_APP_PATIENT_SCOPES,
          grant_type: "password",
        },
        { "Content-Type": "application/x-www-form-urlencoded" }
      );

      sessionStorage.setItem("LoginUserAccessToken", CryptoJS.AES.encrypt(data.access_token, secretKey));
      sessionStorage.setItem("LoginUserRefreshToken", CryptoJS.AES.encrypt(data.refresh_token, secretKey));
      sessionStorage.setItem("LoginUserType", CryptoJS.AES.encrypt("Patient", secretKey));

      navigate("/patient/dashboard");
    } catch {
      setSnackbar({ open: true, msg: "OTP verification failed", type: "error" });
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <Box className="wellness-bg" sx={{ minHeight: "100vh", py: 8 }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Box className="glass-card" sx={{ p: 4 }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <FavoriteRoundedIcon color="primary" />
                <Typography variant="h5" fontWeight={800}>Create Patient Account</Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {[
                    ["title", "Title"], ["fname", "First Name"], ["mname", "Middle Name"],
                    ["lname", "Last Name"], ["phone_contact", "Phone"],
                    ["email", "Email"], ["street", "Street"],
                    ["postal_code", "Postal Code"], ["city", "City"],
                    ["state", "State"], ["height", "Height (cm)"], ["weight", "Weight (kg)"]
                  ].map(([name, label]) => (
                    <Grid item xs={12} sm={4} key={name}>
                      <TextField
                        fullWidth
                        name={name}
                        label={label}
                        value={values[name]}
                        onChange={handleChange}
                        error={!!errors[name]}
                        helperText={errors[name]}
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      name="DOB"
                      label="Date of Birth"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: maxDateString }}
                      value={values.DOB}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Sex</InputLabel>
                      <Select name="sex" value={values.sex} onChange={handleChange}>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(v => !v)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type={showVerifyPassword ? "text" : "password"}
                      label="Verify Password"
                      value={verifyPassword}
                      onChange={(e) => setVerifyPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowVerifyPassword(v => !v)}>
                              {showVerifyPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox checked={values.terms} onChange={handleChange} name="terms" />}
                      label="I agree to the Terms & Conditions"
                    />
                    {errors.terms && <FormHelperText error>{errors.terms}</FormHelperText>}
                  </Grid>

                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" disabled={loading}>
                      {loading ? <CircularProgress size={22} /> : "Create Account"}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              <Typography sx={{ mt: 3, textAlign: "center" }}>
                Already have an account? <Link to="/signin">Sign In</Link>
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* OTP */}
      <Dialog open={otpDialog}>
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography align="center">Enter OTP sent to {values.email}</Typography>
          <OtpInput otp={otp} setOtp={setOtp} />
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.type}>{snackbar.msg}</Alert>
      </Snackbar>
    </>
  );
}
